/**
 * Application model for the Netx instance
 * @class
 */
import Base from '@netx/core/lib/netx-model';
import {getPreference} from '@netx/core-user-next/utils/getPreference';
import StorageLocationsCollection from '../collections/storageLocations';
import {randomChars} from '@netx/core/utils/randomChars';
import {userProfile} from '@netx/core-user/collections/singletons/userProfile';

class App extends Base {
	/**
	 * @type {Netx.Rpc}
	 * @private
	 */
	get rpc() {
		return true;
	}

	/** @type {boolean} */
	get xhrTrackProgress() {
		return true;
	}

	/**
	 * @property {object}  defaults                          - The default attribute values
	 * @property {string}  defaults.description              - The default app description
	 * @property {string}  defaults.clientLogs               - The default app client logs
	 * @property {string}  defaults.ccSelf                   - The defualt copy self (as a boolean string?)
	 * @property {string}  defaults.serverUnresponsiveCount  - DAM-3261 - unavailable server, after 3 alert user
	 * @property {string}  defaults.logo                     - Endpoint to retrieve logo image
	 */
	get defaults() {
		return {
			// for problem reports:
			description: '',
			clientLogs: '',
			ccSelf: 'true',
			serverUnresponsiveCount: 0,
			logo: '/theme/netx/logo?v=' + randomChars(5),
		};
	}

	/**
	 * @property {object}           methods           - The methods available on this model
	 * @property {function|array}   methods.read      - The read method (get)
	 * @property {function|array}   methods.complain  - Sends complaint
	 * @property {function|array}   methods._contact  - Sends contact
	 */
	get methods() {
		return {
			read: ['getProductVersion'],
			_contact: [
				'email',
				'contactArgs.to',
				'contactArgs.from',
				'contactArgs.subject',
				'contactArgs.message',
				'contactArgs.cc',
				'contactArgs.overrideBlock',
			],
		};
	}

	/**
	 * @override
	 * @param {object} attrs Model attributes
	 * @param {object} opts Model options
	 * @listens module:app#app:loaded
	 */
	constructor(attrs, opts) {
		super(attrs, opts);

		/**
		 * Contact arguments
		 * @memberOf Netx.Models.App
		 * @alias contactArgs
		 * @type {object}
		 */
		this.contactArgs = {
			from: '',
			to: [],
			cc: [],
			subject: '',
			message: '',
			overrideBlock: false,
		};

		/**
		 * Storage location
		 * Fetched in user module
		 * @memberOf Netx.Models.App
		 * @alias storageLocations
		 * @type {Netx.Collections.StorageLocations}
		 * @see module:user#user:initialize
		 */
		this.storageLocations = new StorageLocationsCollection([]);
		this.addSubCollection(this.storageLocations);

		// Hey, listen!
		this.listenToOnce(dispatcher, 'app:loaded', () => {
			this.loaded = true;
		});
	}
	/**
	 * @method
	 * @param {object} values Contact submission values
	 * @return {jQuery.Deferred}
	 */
	contact(values) {
		// Check user prefs for email.mailto.rcpt_to property and convert to an array
		const recipient = _.csvToArray(getPreference('email.mailto.rcpt_to'));
		const userEmail = values.emailFrom || userProfile.get('email');

		// Check that "recipient" is an array of values and is not empty
		if (_.isArray(recipient) && !_.isEmpty(recipient)) {
			_.extend(this.contactArgs, {
				from: userEmail,
				subject: values.subject,
				message: values.message,
				to: recipient,
				cc: values.ccSelf ? [userEmail] : [],
			});
			return this._contact();
		} else {
			// Email setting not configured properly.  Let developers know and return rejected promise.
			console.warn(
				'Unable to send email.  Missing valid recipient(s), please configure your email settings',
			);
			return new $.Deferred().reject();
		}
	}
	// /**
	//  * When indicated that the app is ready, deal with the loading screen and anything else that is needed
	//  * before the app can continue on with the UI
	//  * @method
	//  * @return {jQuery.Deferred}
	//  */
	// ready() {
	// 	const d = new $.Deferred();
	// 	const done = () => {
	// 		d.resolve(this);
	// 	};

	// 	Netx.Loading ? Netx.Loading.appLoaded(done) : done();

	// 	return d.promise();
	// }
	// /**
	//  * Controllers can call this and provide a callback and context (usually the controller's module namespace object)
	//  * it will only ever fire once and will remove any previous listener for the same context if called multiple times
	//  * @method
	//  * @param {function} cb Callback to perform when app is ready
	//  * @param {object} context Context to hook the listener to (will default to `window`)
	//  * @listens module:app#app:loaded
	//  */
	// whenReady(cb, context) {
	// 	if (this.loaded) {
	// 		return cb();
	// 	}
	// 	dispatcher.off('app:loaded', context || window);
	// 	dispatcher.once('app:loaded', cb, context || window);
	// }

	/** @type {string} */
	static get __name__() {
		return 'App';
	}
}

export default App;
